import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type { WorkspaceList, ControlHub, ControlAuditEvent } from '@/api/types/cts-workspace.types';

// ── TC2 — the tenant Control Workspace reads ─────────────────────────────────────────────────
//
// ⚠ The `/workspace/` segment is deliberate on the SERVER side too (Trap #11): three legacy
// control routes already own /compliance/controls/*, and they run in parallel until TC6.

export function useControlsWorkspace() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-workspace', 'controls', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/workspace/controls');
      return data.data as WorkspaceList;
    },
    enabled: !!tenantId,
  });
}

export function useControlHub(reference: string | undefined) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-workspace', 'control', tenantId, reference],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/workspace/controls/${reference}`);
      return data.data as ControlHub;
    },
    enabled: !!tenantId && !!reference,
  });
}

/**
 * CAPR — the control's audit history (risk_manager / super_admin only; the caller passes `enabled` false for
 * anyone else rather than draw a refusal). Keyed under 'cts-workspace' so every write that already
 * invalidates the workspace — a cadence change, a start, a decision — refreshes it too.
 */
export function useControlAuditHistory(reference: string | undefined, enabled: boolean) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-workspace', 'control-audit-history', tenantId, reference],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/workspace/controls/${reference}/audit-history`);
      return data.data as ControlAuditEvent[];
    },
    enabled: !!tenantId && !!reference && enabled,
  });
}

// ── The RACI lanes and the bulk operations ────────────────────────────────────────────────────
//
// ⚠ THREE LANES, THREE CALLS, NEVER ONE. Setting a Responsible does not touch the Approver and
// does not assign work. The api enforces that; these hooks simply never offer the shortcut.

/** Set or clear ONE lane on ONE control. `userId: null` clears. */
export function useSetControlRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      controlReference: string; role: 'responsible' | 'accountable'; userId: string | null;
    }) => {
      const { data } = await apiClient.post(
        `/compliance/workspace/controls/${v.controlReference}/role`,
        { role: v.role, userId: v.userId },
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
      qc.invalidateQueries({ queryKey: ['cts-activity'] });
    },
  });
}

/**
 * Set or clear ONE lane across MANY controls.
 *
 * ⚠ Returns `{ set, replaced, skipped }` — `replaced` is how many already had someone, counted in
 * the same statement that wrote, so the number shown to the user describes the write that happened.
 */
export function useBulkSetControlRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      controlReferences: string[]; role: 'responsible' | 'accountable'; userId: string | null;
    }) => {
      const { data } = await apiClient.post('/compliance/workspace/controls/bulk-role', v);
      // ⚠ `set` carries each control's PREVIOUS holder, not just its reference — an undo needs to
      //    know who each one had before, and a count cannot be reversed.
      return data.data as {
        set: { reference: string; previous: string | null }[];
        replaced: number; skipped: string[];
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
      qc.invalidateQueries({ queryKey: ['cts-activity'] });
    },
  });
}

/**
 * Assign workflow work across MANY controls.
 *
 * ⚠ THE RESULT IS THE POINT. This replaces a client-side loop that threw every outcome away —
 * and once V1051 made an already-assigned control a 409, the controls a user most needed to hear
 * about were exactly the silent ones. `skipped` names them.
 */
export function useBulkAssign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { controlReferences: string[]; assigneeUserId: string }) => {
      const { data } = await apiClient.post('/compliance/workspace/tasks/bulk', v);
      return data.data as {
        assigned: { reference: string; id: string }[];
        skipped: { reference: string; reason: string }[];
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
      qc.invalidateQueries({ queryKey: ['cts-tasks'] });
      qc.invalidateQueries({ queryKey: ['cts-activity'] });
    },
  });
}
